import { useMemo, useState } from "react";
import { useTran } from "@/hooks";
import { Check, X, Pencil, Trash2, Minus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DataTable, MultiSelect } from "@/components/common";
import { buildTableDefaults } from "@/components/common/data-table-helpers";
import {
  ADMIN_ROLES,
  buildPermissionRows,
  CLIENT_ROLE_GROUPS,
  formatRoleLabel,
  MODULE_TABLE_HEADERS,
  normalizeId,
} from "../utils/roles-permissions";
import { cn } from "@/lib/utils/index";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import { getRestrictedActions } from "@/helpers";
import { EXCLUDED_PERMISSIONS_IDS } from "@/helpers/permissionConstants";
import { useEffect } from "react";

const renderPermissionFlag = (value) => (
  <span className="inline-flex items-center justify-center rounded px-2 py-0.5 text-[11px] font-medium">
    {value ? (
      <Check className="h-3.5 w-3.5 text-emerald-600" />
    ) : (
      <X className="h-3.5 w-3.5 text-red-600" />
    )}
  </span>
);

const RolesPermissionsTable = ({
  data = [],
  loading = false,
  isAdminGroup = false,
  companyId = "",
  roleUsageById = {},
  roleUsageLoading = false,
  onEdit,
  onDelete,
}) => {
  const { t } = useTran();
  const { emptyMessage, loadingMessage, getRowProps } = buildTableDefaults({
    t,
    emptyMessageKey: "No roles available.",
  });

  const userStr = JSON.parse(localStorage.getItem("user"));
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  const NORMALIZED_CLIENT_ROLE_GROUPS = new Set(
    [...CLIENT_ROLE_GROUPS].map(normalizeId),
  );

  const isSystemRole = (role) => {
    return NORMALIZED_CLIENT_ROLE_GROUPS.has(normalizeId(role));
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // ---------- FILTER STATE ----------
  const [filters, setFilters] = useState({
    roles: [],
  });

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return filters.roles.length === 0 || filters.roles.includes(item.role);
    });
  }, [data, filters]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      roles: [],
    });

    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        key: "index",
        header: t("No."),
        cellClassName: "align-top",
        render: (item, index) => (
          <span className="font-medium text-foreground">{index + 1}</span>
        ),
      },
      {
        key: "role",
        header: t("Role Name"),
        cellClassName: "align-top",
        render: (item) => (
          <span className="font-medium text-foreground">
            {ADMIN_ROLES.includes(userStr.role) || isSystemRole(item.role)
              ? formatRoleLabel(item.role)
              : item.role}
          </span>
        ),
      },
      {
        key: "permissions",
        header: t("Permissions"),
        cellClassName: "align-top",
        render: (item) => {
          const permissionRows = buildPermissionRows(item.permissions).filter(
            (row) => !EXCLUDED_PERMISSIONS_IDS.includes(row.key),
          );
          const isUserCreatedRole = !isSystemRole(item.role);
          return (
            <div className="w-full overflow-auto">
              {permissionRows.length > 0 ? (
                <div className="min-w-[420px] overflow-hidden rounded-radius border border-border">
                  <div className="grid grid-cols-6 bg-muted/40 px-3 py-2 text-[14px] font-semibold tracking-wide text-muted-foreground text-primary">
                    {MODULE_TABLE_HEADERS.map((header) => (
                      <span
                        key={header}
                        className={cn(
                          "font-medium",
                          header === "Module" ? "col-span-2" : "text-center",
                        )}
                      >
                        {t(header)}
                      </span>
                    ))}
                  </div>
                  {permissionRows.map((row) => {
                    const restricted = getRestrictedActions(
                      row.key,
                      null,
                      isUserCreatedRole,
                    );

                    const filteredActions = {
                      view: row.actions.view,
                      create: restricted.includes("create")
                        ? false
                        : row.actions.create,
                      update: restricted.includes("update")
                        ? false
                        : row.actions.update,
                      delete: restricted.includes("delete")
                        ? false
                        : row.actions.delete,
                    };

                    return (
                      <div
                        key={`${item.role}-${row.label}`}
                        className="grid grid-cols-6 items-center gap-2 border-t border-border px-2 py-1 text-xs"
                      >
                        <span className="col-span-2 text-foreground">
                          {t(row.label)}
                        </span>

                        {/* VIEW */}
                        <span className="text-center">
                          {renderPermissionFlag(filteredActions.view)}
                        </span>

                        {/* CREATE */}
                        <span className="text-center">
                          {restricted.includes("create") ? (
                            <Minus
                              size={10}
                              className="mx-auto text-muted-foreground"
                            />
                          ) : (
                            renderPermissionFlag(filteredActions.create)
                          )}
                        </span>

                        {/* UPDATE */}
                        <span className="text-center">
                          {restricted.includes("update") ? (
                            <Minus
                              size={10}
                              className="mx-auto text-muted-foreground"
                            />
                          ) : (
                            renderPermissionFlag(filteredActions.update)
                          )}
                        </span>

                        {/* DELETE */}
                        <span className="text-center">
                          {restricted.includes("delete") ? (
                            <Minus
                              size={10}
                              className="mx-auto text-muted-foreground"
                            />
                          ) : (
                            renderPermissionFlag(filteredActions.delete)
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t("No permissions assigned")}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        hidden:
          isAdminGroup ||
          (!permissions.includes("roles_permissions:update") &&
            !permissions.includes("roles_permissions:delete")),
        cellClassName: "align-top",
        render: (item) => (
          <div className="flex justify-end -space-x-1">
            {(() => {
              const roleId = normalizeId(
                item?.raw?._id ||
                  item?.raw?.id ||
                  item?.raw?.key ||
                  item?.raw?.role,
              );
              const isCompanyRole =
                normalizeId(item?.raw?.companyId) === companyId;
              const isRoleUsed = Boolean(roleId && roleUsageById[roleId]);
              const disableDelete =
                roleUsageLoading || !isCompanyRole || isRoleUsed;
              const deleteTitle = isRoleUsed
                ? t("Role is assigned to users.")
                : roleUsageLoading
                  ? t("Checking role usage...")
                  : undefined;

              return (
                <>
                  <Can
                    resource="roles_permissions"
                    action="update"
                    userPermissions={permissions}
                    enabled={!isAdminGroup && !isSystemRole(item.role)}
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-yellow-500 hover:bg-yellow-50"
                      icon={Pencil}
                      onClick={() => onEdit?.(item)}
                    />
                  </Can>
                  <Can
                    resource="roles_permissions"
                    action="delete"
                    userPermissions={permissions}
                    enabled={!isAdminGroup && !isSystemRole(item.role)}
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => onDelete?.(item)}
                      disabled={disableDelete}
                      title={deleteTitle}
                      icon={Trash2}
                    />
                  </Can>
                </>
              );
            })()}
          </div>
        ),
      },
    ],
    [
      t,
      isAdminGroup,
      companyId,
      roleUsageById,
      roleUsageLoading,
      onEdit,
      onDelete,
    ],
  );

  return (
    <div>
      <div className="w-full pb-4">
        <div className="w-full pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full max-w-sm space-y-2">
              <label className="ml-1 text-sm font-medium text-primary">
                {t("Filter By Role Name")}
              </label>

              <MultiSelect
                options={Array.from(new Set(data.map((item) => item.role))).map(
                  (role) => ({
                    label: formatRoleLabel(role),
                    value: role,
                  }),
                )}
                value={filters.roles}
                onChange={(val) => setFilters({ roles: val })}
                placeholder={t("Filter by role name...")}
                maxDisplay={2}
                maxLabelDisplay={10}
              />
            </div>

            {filters.roles?.length > 0 && (
              <Button
                size="lg"
                variant="outline"
                onClick={resetFilters}
                className="h-11 px-4"
              >
                {t("Clear Filters")}
              </Button>
            )}
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        getRowKey={(row) => row.id || row.role}
        getRowProps={getRowProps}
        pagination={{
          page,
          pageSize,
          total: filteredData.length,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [5, 10, 15],
          disabled: loading,
          labels: {
            itemsPerPage: t("Items per page"),
            previous: t("Previous"),
            next: t("Next"),
            pageLabel: ({ page: current, totalPages }) =>
              `${current} / ${totalPages}`,
          },
        }}
      />
    </div>
  );
};

export default RolesPermissionsTable;
